import { SellerMeta } from "./Meta";
import { Note } from "./Note";
import { PartnerFeeFormula } from "./PartnerFeeFormula";
import { SellerDefaultGateway } from "./PaymentGateway";
import { RemoteGatewayType } from "./RemoteGateway";
import { Schedule } from "./Schedule";
import { SelectedExperiences } from "./SelectedExperiences";
import { SellerPaymentMethod } from "./SellerPaymentMethod";
import { StripeAccount } from "./StripeAccount";
import { SubscriptionPlan } from "./SubscriptionPlan";
import { Term } from "./Term";
import { User, UserRole } from "./User";
import { UserPreferences } from "./UserPreferences";

type MixedRole = FeatureRole | UserRole;

// `Omit` type allows us to construct a type by picking all properties from `User` and then removing `roles` key.
export interface Seller extends Omit<User, "roles"> {
    communicationEmail: string;
    website: string;
    roles: MixedRole[];
    timezoneName: string;
    locale: string;
    autoAccept: SelectedExperiences;
    preferences: UserPreferences;
    paymentMethods?: SellerPaymentMethod[];
    currency: string;
    balance?: number; // Optional - not available for all users.
    meta: SellerMeta;
    city: string;
    state?: string;
    country?: string;
    streetAddress?: string;
    company?: string;
    picture: { id: ID };
    statementDescriptor: string;
    countryCode: string;
    acceptedCards: string[];
    allowedGateways: RemoteGatewayType[];
    stripeAccount: StripeAccount;
    customerNotes: { [id: ID]: { [id: ID]: Note } };
    partnerFeeFormulas: PartnerFeeFormula[];
    terms: Term;
    emailFooter?: string;
    defaultGateway?: SellerDefaultGateway;
    blackoutSchedules?: Schedule[];
    subscriptionPlan?: SubscriptionPlan;
}

export enum FeatureRole {
    ABANDONS = "ROLE_ABANDONS",
    WAITLIST = "ROLE_WAITLIST",
    WAIVER = "ROLE_WAIVER",
    VIRTUAL_MEETING = "ROLE_VIRTUAL_MEETING",
    GIFT = "ROLE_GIFT",
    PACKAGES = "ROLE_PACKAGES",
    MEMBERSHIPS = "ROLE_MEMBERSHIPS",
    MERCHANDISE = "ROLE_WARES",
    POS = "ROLE_POS",
    TICKETS = "ROLE_TICKETS",
    API_DETAILS = "ROLE_API_DETAILS",
    CONVERSION_TRACKING = "ROLE_CONVERSION_TRACKING",
    FACEBOOK_PIXEL_TRACKING = "ROLE_FACEBOOK_PIXEL_TRACKING",
    CROSS_ACCOUNT_SELLING = "ROLE_CROSS_ACCOUNT_SELLING",
    TIERED_PRICING = "ROLE_TIERED_PRICING",
    EMV = "ROLE_EMV",
    EMBEDDED_CHECKOUT = "ROLE_EMBEDDED_CHECKOUT",
    CUSTOM_NOTIFICATION_TIMING = "ROLE_CUSTOM_NOTIFICATION_TIMING",
    ANALYTICS = "ROLE_ANALYTICS",
    BARCODE = "ROLE_BARCODE",
    SELLER_INVENTORY = "ROLE_SELLER_INVENTORY",
    GUIDE_MANAGER = "ROLE_GUIDE_MANAGER",
    AFFILIATES = "ROLE_AFFILIATES",
    STORE_CREDIT = "ROLE_STORE_CREDIT",
    REALTIME_UPDATES = "ROLE_REALTIME_UPDATES",
    MULTI_LANGUAGE_SUPPORT = "ROLE_MULTI_LANGUAGE",
    ACCESS_MANAGER = "ROLE_ACCESS_MANAGER",
    INVOICE = "ROLE_INVOICE",
    LIVE_CHAT = "ROLE_LIVE_CHAT",
    XCHAT = "ROLE_XCHAT",
    SPLIT_PAYMENT = "ROLE_SPLIT_PAYMENT",
    TUTORIALS = "ROLE_TUTORIALS",
    REPORT_TEMPLATES = "ROLE_REPORT_TEMPLATES",
    TELEMETRY = "ROLE_TELEMETRY",
    X2_CHECKOUT = "ROLE_X2_CHECKOUT",
    EQUIPMENT_MANAGER = "ROLE_EQUIPMENT_MANAGER",
    PRODUCT_COLLECTIONS = "ROLE_PRODUCT_COLLECTIONS",
    CANCELLATION_PROTECTION = "ROLE_CANCELLATION_PROTECTION",
    XSITE = "ROLE_XSITE",
}
